/**
 * The composer's skill chip. A skill is a procedure the agent follows
 * (orthogonal to the Agent / Plan mode); picking one here uses it for the
 * next message, and Pin keeps it for every later turn. With none selected the
 * trigger is a small icon; with one it is a removable chip that says which.
 * The popover lists the firm's saved automations, then the case Explore
 * catalog in the same groups as the Case Automations board, then built-ins
 * (a firm skill that replaces a built-in says so and the built-in is left
 * out). A firm skill's row click selects it (the attorney then types the
 * request) and its Play button *runs* it (a run is recorded and the seed
 * message is sent at once). An Explore starter is not a saved skill: its row
 * sends the starter's steps. A built-in needs no request: with `onRunSkill`,
 * its row click runs it.
 */
import { Check, Pin, Play, Sparkles, X } from "lucide-react";
import { useState, type FC, type MouseEvent } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { isOffered } from "@/features/agents/skillsForFocus";
import type { ExploreGroup } from "@/features/automations/board/caseExploreStarters";
import type { StarterSkill } from "@/features/automations/components/skillModel";
import { automationsPath } from "@/features/automations/routes";
import type { FirmSkillSummary } from "@/features/firmKnowledge/types";
import { cn } from "@/lib/utils";

export type SkillPickerProps = {
  skills: FirmSkillSummary[];
  skillId: string | null;
  onSkillChange: (skillId: string | null) => void;
  /** The chip's skill stays for every later turn. */
  pinned?: boolean;
  /** Pin / unpin the chip's skill; omit to hide the toggle. */
  onPinChange?: (pinned: boolean) => void;
  /** Run the skill on this case now (records a run, sends the seed). */
  onRunSkill?: (skill: FirmSkillSummary) => void;
  /**
   * Case Explore groups, in the same order as the Case Automations board.
   * Firm copies stay in their own group and do not replace these.
   */
  exploreGroups?: ExploreGroup[];
  /** Run an Explore starter on this case (sends its steps; not a saved skill). */
  onRunStarter?: (starter: StarterSkill) => void;
  /** Firm display name; the group heading is `{name}'s Automations`. */
  firmName?: string | null;
  disabled?: boolean;
};

const possessive = (name: string): string =>
  /s$/i.test(name) ? `${name}'` : `${name}'s`;

const skillsHeading = (firmName?: string | null): string => {
  const name = firmName?.trim();
  if (!name) return "Firm's Automations";
  const titled =
    name === name.toLowerCase()
      ? name.replace(/\b\w/g, (c) => c.toUpperCase())
      : name;
  return `${possessive(titled)} Automations`;
};

const HEADING = "truncate px-1 pt-1.5 text-left text-xs font-medium text-fg-3";

const CHIP =
  "h-6 gap-1 rounded-full border-transparent bg-purple-soft px-2 text-sm font-medium text-purple shadow-none hover:bg-purple/20 hover:text-purple aria-expanded:bg-purple/20";

export const SkillPicker: FC<SkillPickerProps> = ({
  skills,
  skillId,
  onSkillChange,
  pinned = false,
  onPinChange,
  onRunSkill,
  exploreGroups = [],
  onRunStarter,
  firmName,
  disabled
}) => {
  const [open, setOpen] = useState(false);
  const selected = skillId
    ? skills.find((s) => s.id === skillId) ?? null
    : null;
  const offered = skills.filter(isOffered);
  // The same saved skill can be listed once. Distinct copies (different ids,
  // even with the same title) stay, each under the firm's group.
  const unique = (list: FirmSkillSummary[]): FirmSkillSummary[] => {
    const seen = new Set<string>();
    return list.filter((s) => (seen.has(s.id) ? false : !!seen.add(s.id)));
  };
  const firmSkills = unique(offered.filter((s) => !s.builtin));
  const builtinSkills = unique(offered.filter((s) => s.builtin));
  const exploreCount = exploreGroups.reduce((n, g) => n + g.starters.length, 0);

  const renderSkill = (skill: FirmSkillSummary) => {
    const active = skill.id === skillId;
    const runnable = !!onRunSkill && !skill.builtin;
    return (
      <CommandItem
        key={skill.id}
        value={`${skill.id} ${skill.title} ${skill.slug} ${skill.description}`}
        onSelect={() => {
          setOpen(false);
          if (skill.builtin && onRunSkill) onRunSkill(skill);
          else onSkillChange(active ? null : skill.id);
        }}
        className={cn("min-w-0 items-start gap-2 py-1.5", runnable && "pr-8")}
        data-testid={`skill-option-${skill.id}`}
      >
        <Sparkles className="mt-0.5 size-3.5 shrink-0 text-purple" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-xs font-medium">
            <span className="truncate">{skill.title}</span>
            {active ? <Check className="size-3 shrink-0" /> : null}
          </div>
          {skill.description ? (
            <p className="line-clamp-2 text-xs text-muted-foreground">
              {skill.description}
            </p>
          ) : null}
          {skill.replaces_title ? (
            <p
              className="text-xs text-fg-3"
              data-testid={`skill-replaces-${skill.id}`}
            >
              Replaces built-in {skill.replaces_title}
            </p>
          ) : null}
        </div>
        {runnable ? (
          <CommandShortcut className="absolute top-1.5 right-2 tracking-normal">
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="size-6 shrink-0 rounded-full text-fg-3 hover:bg-muted hover:text-foreground"
              aria-label={`Run ${skill.title} on this case`}
              data-testid={`skill-run-${skill.id}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
                onRunSkill(skill);
              }}
            >
              <Play className="size-3" />
            </Button>
          </CommandShortcut>
        ) : null}
      </CommandItem>
    );
  };

  const renderStarter = (starter: StarterSkill) => {
    const Icon = starter.icon;
    return (
      <CommandItem
        key={starter.id}
        value={`${starter.title} ${starter.blurb} ${starter.category}`}
        onSelect={() => {
          setOpen(false);
          onRunStarter?.(starter);
        }}
        className="min-w-0 items-start gap-2 py-1.5"
        data-testid={`skill-starter-${starter.id}`}
      >
        <Icon className="mt-0.5 size-3.5 shrink-0 text-purple" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium">{starter.title}</div>
          {starter.blurb ? (
            <p className="line-clamp-2 text-xs text-fg-3">{starter.blurb}</p>
          ) : null}
        </div>
      </CommandItem>
    );
  };

  const clear = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSkillChange(null);
  };
  const togglePin = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onPinChange?.(!pinned);
  };
  const scopeLabel = pinned ? "Pinned" : "This message only";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            {selected ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                className={cn(CHIP, "pr-1")}
                aria-label={`Using automation ${selected.title} (${scopeLabel})`}
                data-testid="skill-chip"
                data-skill-id={selected.id}
                data-pinned={pinned ? "" : undefined}
              >
                <Sparkles className="size-3" />
                <span className="max-w-40 truncate">{selected.title}</span>
                {onPinChange ? (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-pressed={pinned}
                    aria-label={pinned ? "Unpin automation" : "Pin automation"}
                    data-testid="skill-chip-pin"
                    onClick={togglePin}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onPinChange(!pinned);
                      }
                    }}
                    className="ml-0.5 inline-flex items-center gap-0.5 rounded-full px-1 text-xs font-normal opacity-80 hover:bg-purple/20"
                  >
                    <Pin className={cn("size-3", pinned && "fill-current")} />
                    {scopeLabel}
                  </span>
                ) : null}
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Clear automation"
                  data-testid="skill-chip-clear"
                  onClick={clear}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSkillChange(null);
                    }
                  }}
                  className="ml-0.5 inline-flex size-4 items-center justify-center rounded-full hover:bg-purple/20"
                >
                  <X className="size-3" />
                </span>
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                disabled={disabled}
                className="size-6 rounded-full text-fg-3 hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground"
                aria-label="Use an automation"
                data-testid="skill-picker-trigger"
              >
                <Sparkles className="size-3.5" />
              </Button>
            )}
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">
          {selected
            ? `Using: ${selected.title}. ${
                pinned
                  ? "Pinned for every message."
                  : "For your next message only; Pin keeps it."
              }`
            : "Use an automation"}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-80 p-0"
        data-testid="skill-picker"
      >
        <Command>
          <CommandInput
            placeholder="Search automations…"
            className="h-8 text-xs"
          />
          <CommandList>
            <CommandEmpty className="px-3 py-4 text-xs text-fg-3">
              {offered.length || exploreCount
                ? "No automation matches."
                : "Your firm has no active automations yet."}
            </CommandEmpty>
            {firmSkills.length ? (
              <>
                <div className={HEADING} data-testid="skill-picker-heading">
                  {skillsHeading(firmName)}
                </div>
                <CommandGroup>{firmSkills.map(renderSkill)}</CommandGroup>
              </>
            ) : null}
            {exploreGroups.map((group) => (
              <div key={group.title}>
                <div
                  className={HEADING}
                  data-testid="skill-picker-explore-group"
                >
                  {group.title}
                </div>
                <CommandGroup>{group.starters.map(renderStarter)}</CommandGroup>
              </div>
            ))}
            {builtinSkills.length ? (
              <>
                <div className={HEADING} data-testid="skill-picker-builtin">
                  Built-In
                </div>
                <CommandGroup>{builtinSkills.map(renderSkill)}</CommandGroup>
              </>
            ) : null}
          </CommandList>
          <div className="border-t px-2 py-1.5">
            <Link
              to={automationsPath()}
              className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              data-testid="skill-picker-manage"
              onClick={() => setOpen(false)}
            >
              Manage automations…
            </Link>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
